import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { fetchBlogPost, fetchBlogPosts } from '../services/api';
import { BlogPostDetail, BlogPost } from '../types/blog';

const isHtmlContent = (content: string): boolean => {
    const trimmed = content.trim();
    return trimmed.startsWith('<') && /<[a-z][\s\S]*>/i.test(trimmed);
};

const BlogDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<BlogPostDetail | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Map i18n language code to API language code
    const getLanguageCode = (lang: string): string => {
        const langCode = lang.split('-')[0].toLowerCase();
        const supportedLanguages = ['en', 'zh', 'fr', 'es'];
        return supportedLanguages.includes(langCode) ? langCode : 'en';
    };

    // Format date for display
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Load blog post details
    useEffect(() => {
        const loadBlogPost = async () => {
            if (!slug) {
                setError('No blog post slug provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const currentLang = getLanguageCode(i18n.language);
                const fetchedPost = await fetchBlogPost(slug, currentLang, 'en');
                setPost(fetchedPost);

                // Fetch related posts from the same category
                if (fetchedPost.category) {
                    const relatedResponse = await fetchBlogPosts({
                        lang: currentLang,
                        fallbackLang: 'en',
                        category: fetchedPost.category,
                        pageSize: 3,
                    });

                    // Filter out the current post
                    const filtered = relatedResponse.posts.filter(p => p.slug !== slug);
                    setRelatedPosts(filtered.slice(0, 3));
                }
            } catch (err) {
                console.error('Failed to load blog post:', err);
                setError('Failed to load blog post');
            } finally {
                setLoading(false);
            }
        };

        loadBlogPost();
    }, [slug, i18n.language]);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (loading) {
        return (
            <div className="main-wrapper">
                <div style={{ textAlign: 'center', padding: '5rem' }}>
                    <p>{t('blog.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="main-wrapper">
                <div style={{ textAlign: 'center', padding: '5rem' }}>
                    <h2>{t('blogDetail.notFound', 'Blog post not found')}</h2>
                    <Link to="/blog" style={{
                        color: '#FF6B9D',
                        textDecoration: 'none',
                        fontWeight: '600',
                        marginTop: '1rem',
                        display: 'inline-block'
                    }}>
                        {t('blogDetail.backToBlog', 'Back to Blog')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="main-wrapper">
            {/* Hero Banner Section */}
            <div className="section-hero-banar is-blog">
                <div className="padding-global">
                    <div className="container-large">
                        <div className="padding-section-medium">
                            <div className="hero-banner-component">
                                <div className="hero-banner-content-wrapper">
                                    <h2 className="heading-style-h2">{post.title}</h2>
                                    <div className="hero-banar-content" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        marginTop: '1rem'
                                    }}>
                                        <Link to="/" className="home-link" style={{
                                            color: '#666',
                                            textDecoration: 'none',
                                            fontWeight: '500'
                                        }}>
                                            {t('navbar.home', 'Home')}
                                        </Link>
                                        <div className="hero-banar-dot" style={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: '#FF6B9D'
                                        }}></div>
                                        <Link to="/blog" style={{
                                            color: '#666',
                                            textDecoration: 'none',
                                            fontWeight: '500'
                                        }}>
                                            {t('blog.pageTitle', 'Blog')}
                                        </Link>
                                        <div className="hero-banar-dot" style={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: '#FF6B9D'
                                        }}></div>
                                        <div className="hero-banar-text" style={{
                                            color: '#FF6B9D',
                                            fontWeight: '600'
                                        }}>
                                            {post.category_name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Blog Detail Section */}
            <div className="section-blog-detail">
                <div className="padding-global">
                    <div className="container-large">
                        <div className="padding-section-large">
                            {/* Blog Content */}
                            <div className="blog-content-wrapper">
                                {/* Blog Meta Information */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1.5rem',
                                    marginBottom: '2rem',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        color: '#6B7280',
                                        fontSize: '0.95rem'
                                    }}>
                                        <img
                                            src="https://cdn.prod.website-files.com/65d1995bebc2cbfa914e45d5/65d6d4d037b79065abed9fcc_Frame%2045.svg"
                                            loading="lazy"
                                            alt=""
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span>{formatDate(post.date)}</span>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <span style={{
                                            padding: '0.5rem 1rem',
                                            borderRadius: '50px',
                                            border: '2px solid #FF6B9D',
                                            color: '#FF6B9D',
                                            fontWeight: '600',
                                            fontSize: '0.9rem'
                                        }}>
                                            {post.category_name}
                                        </span>
                                    </div>
                                    {post.reading_time_minutes && (
                                        <div style={{
                                            color: '#6B7280',
                                            fontSize: '0.95rem'
                                        }}>
                                            {post.reading_time_minutes} {t('blogDetail.minRead', 'min read')}
                                        </div>
                                    )}
                                </div>
                                {/* Featured Image */}
                                <div style={{
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    marginBottom: '3rem',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                                }}>
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: '500px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                {/* Excerpt/Introduction */}
                                <div style={{
                                    fontSize: '1.15rem',
                                    lineHeight: '1.8',
                                    color: '#374151',
                                    marginBottom: '2rem',
                                    fontWeight: '500',
                                    fontStyle: 'italic',
                                    paddingLeft: '1.5rem',
                                    borderLeft: '4px solid #FF6B9D'
                                }}>
                                    {post.excerpt}
                                </div>

                                {/* Main Content */}
                                <div className="markdown-body" style={{
                                    fontSize: '1.05rem',
                                    lineHeight: '1.8',
                                    color: '#4B5563'
                                }}>
                                    {post.content ? (
                                        isHtmlContent(post.content) ? (
                                            <div dangerouslySetInnerHTML={{ __html: post.content }} />
                                        ) : (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                rehypePlugins={[rehypeRaw]}
                                            >
                                                {post.content}
                                            </ReactMarkdown>
                                        )
                                    ) : (
                                        <div>
                                            <p style={{ marginBottom: '1.5rem' }}>
                                                {post.excerpt}
                                            </p>
                                            <p style={{ marginBottom: '1.5rem' }}>
                                                {t('blogDetail.contentPlaceholder1', 'This is a comprehensive article about child development and parenting strategies. Our expert team has compiled the latest research and practical advice to help you support your child\'s growth journey.')}
                                            </p>
                                            <p style={{ marginBottom: '1.5rem' }}>
                                                {t('blogDetail.contentPlaceholder2', 'Understanding developmental milestones is crucial for every parent. By recognizing and celebrating each achievement, you can provide the right support at the right time, fostering healthy growth and development.')}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Back to Blog Link */}
                                <div style={{
                                    marginTop: '3rem',
                                    paddingTop: '2rem',
                                    borderTop: '2px solid #E5E5E5'
                                }}>
                                    <Link
                                        to="/blog"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            color: '#FF6B9D',
                                            fontWeight: '600',
                                            textDecoration: 'none',
                                            fontSize: '1rem',
                                            transition: 'gap 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.gap = '1rem';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.gap = '0.5rem';
                                        }}
                                    >
                                        <img
                                            src="https://cdn.prod.website-files.com/65d1995bebc2cbfa914e45d5/65d6d655572382ae0996564c_arrow-right.svg"
                                            loading="lazy"
                                            alt=""
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                transform: 'rotate(180deg)'
                                            }}
                                        />
                                        <span>{t('blogDetail.backToBlog', 'Back to Blog')}</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Posts Section */}
            {relatedPosts.length > 0 && (
                <div className="section-related-posts" style={{
                    paddingTop: '3rem',
                    paddingBottom: '3rem'
                }}>
                    <div className="padding-global">
                        <div className="container-large">
                            <div className="padding-section-medium">
                                <div className="blog-content-wrapper">
                                    {/* Section Heading */}
                                    <div style={{ marginBottom: '2rem' }}>
                                        <h3 className="heading-style-h3" style={{
                                            fontSize: '2rem',
                                            fontWeight: '700',
                                            marginBottom: '0.5rem'
                                        }}>
                                            {t('blogDetail.relatedPosts', 'Related Articles')}
                                        </h3>
                                        <div className="line-color-pink" style={{
                                            width: '100px',
                                            height: '8px',
                                            background: 'linear-gradient(90deg, #FF6B9D 0%, #FFA8C5 100%)',
                                            marginTop: '0.5rem'
                                        }}></div>
                                    </div>

                                    {/* Related Posts Grid */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                        gap: '2rem'
                                    }}>
                                    {relatedPosts.map((relatedPost) => (
                                        <div
                                            key={relatedPost.id}
                                            className="blog-collection-item"
                                            style={{
                                                opacity: 1,
                                                transform: 'translate3d(0px, 0px, 0px) scale3d(1, 1, 1)',
                                            }}
                                        >
                                            <div className="blog-item" style={{
                                                background: 'white',
                                                borderRadius: '20px',
                                                overflow: 'hidden',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-8px)';
                                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(255,107,157,0.15)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                            }}
                                            >
                                                <img
                                                    src={relatedPost.image}
                                                    loading="lazy"
                                                    alt={relatedPost.title}
                                                    className="blog-image"
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <div className="blog-details-content" style={{
                                                    padding: '1.5rem',
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem',
                                                        marginBottom: '1rem'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#6B7280'
                                                        }}>
                                                            {formatDate(relatedPost.date)}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#FF6B9D',
                                                            fontWeight: '600'
                                                        }}>
                                                            {relatedPost.category_name}
                                                        </div>
                                                    </div>
                                                    <h5 style={{
                                                        fontSize: '1.1rem',
                                                        fontWeight: '700',
                                                        marginBottom: '0.75rem',
                                                        lineHeight: '1.4',
                                                        color: '#1F2937'
                                                    }}>
                                                        {relatedPost.title}
                                                    </h5>
                                                    <p style={{
                                                        fontSize: '0.9rem',
                                                        color: '#6B7280',
                                                        lineHeight: '1.6',
                                                        flex: 1
                                                    }}>
                                                        {relatedPost.excerpt}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogDetailPage;
